import React from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { DataGridPro, GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from '@mui/x-data-grid-pro';
import { AppTheme } from '../../common/theme';
import { auditLogClearFiltersButtonSx, DateFilterTextField } from '../AuditLogFilterControls.jsx';
import { ApprovalAuditChangeDetails } from './ApprovalAuditChangeDetails.jsx';

const SOURCE_TYPE_OPTIONS = [
    { value: '', label: 'All Types' },
    { value: 'Rule', label: 'Rule' },
    { value: 'Group', label: 'Group' },
    { value: 'Setup', label: 'Approval Setup' }
];

const ACTION_OPTIONS = [
    { value: '', label: 'All Actions' },
    { value: 'Created', label: 'Created' },
    { value: 'Updated', label: 'Updated' },
    { value: 'Deactivated', label: 'Deactivated' }
];

const DEFAULT_PAGE_SIZE = 50;

const getOptionLabel = (options, value) => {
    const option = options.find((item) => item.value === value);
    return option ? option.label : '';
};

const getSourceTypeLabel = (value) => getOptionLabel(SOURCE_TYPE_OPTIONS, value) || value || '';

const formatUserLabel = (users, userId) => {
    if (!userId) {
        return 'All Users';
    }

    const user = users.find((item) => item.id === userId);
    if (!user) {
        return userId;
    }

    return user.PresentationName || user.UserName || `${user.FirstName || ''} ${user.LastName || ''}`.trim();
};

const formatUserName = (row) => {
    const name = `${row?.FirstName || ''} ${row?.LastName || ''}`.trim();
    return name || row?.UserId || '';
};

const formatEventDateCell = (value) => {
    if (!value) {
        return { raw: null, display: '' };
    }

    // API returns a localized date/time string; display it directly to preserve server formatting.
    if (typeof value === 'string') {
        return {
            raw: new Date(value),
            display: value
        };
    }

    const date = new Date(value);
    return {
        raw: date,
        display: date.toLocaleString()
    };
};

const renderEventDateCell = (params) => {
    const display = params.value?.display || '';
    const precise = params.row?.EventDatePrecise;

    if (!display) {
        return '';
    }

    if (!precise || precise === display) {
        return display;
    }

    return (
        <Tooltip title={precise}>
            <span>{display}</span>
        </Tooltip>
    );
};

const DETAIL_PANEL_TOGGLE_FIELD = GRID_DETAIL_PANEL_TOGGLE_COL_DEF.field;

const auditLogGridToggleButtonSx = {
    minWidth: '28px !important',
    minHeight: '28px !important',
    width: 28,
    height: 28,
    marginTop: '0 !important',
    padding: '4px !important',
    '&:hover': {
        backgroundColor: 'transparent'
    }
};

export class ApprovalAuditLog extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            rows: [],
            users: [],
            loading: true,
            resultCount: 0,
            page: 0,
            filters: {
                startDate: '',
                endDate: '',
                sourceType: '',
                action: '',
                userId: '',
                sourceName: ''
            },
            appliedFilters: {
                startDate: '',
                endDate: '',
                sourceType: '',
                action: '',
                userId: '',
                sourceName: ''
            },
            detailPanelExpandedRowIds: [],
            detailPanelWidth: null
        };

        this.gridContainerRef = React.createRef();
        this.resizeObserver = null;

        this.loadUsers = this.loadUsers.bind(this);
        this.loadAuditLog = this.loadAuditLog.bind(this);
        this.handleFilterChange = this.handleFilterChange.bind(this);
        this.applyFilters = this.applyFilters.bind(this);
        this.clearFilters = this.clearFilters.bind(this);
        this.handlePaginationChange = this.handlePaginationChange.bind(this);
        this.handleRowClick = this.handleRowClick.bind(this);
        this.handleDetailPanelExpandedRowIdsChange = this.handleDetailPanelExpandedRowIdsChange.bind(this);
        this.renderDetailPanel = this.renderDetailPanel.bind(this);
        this.getRowId = this.getRowId.bind(this);
        this.updateDetailPanelWidth = this.updateDetailPanelWidth.bind(this);
        this.observeGridContainer = this.observeGridContainer.bind(this);
    }

    componentDidMount() {
        this.loadUsers();
        this.loadAuditLog();
        this.observeGridContainer();
    }

    componentWillUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
    }

    observeGridContainer() {
        if (!this.gridContainerRef.current || typeof ResizeObserver === 'undefined') {
            this.updateDetailPanelWidth();
            return;
        }

        this.updateDetailPanelWidth();
        this.resizeObserver = new ResizeObserver(() => {
            this.updateDetailPanelWidth();
        });
        this.resizeObserver.observe(this.gridContainerRef.current);
    }

    updateDetailPanelWidth() {
        if (!this.gridContainerRef.current) {
            return;
        }

        const width = this.gridContainerRef.current.clientWidth;
        if (width !== this.state.detailPanelWidth) {
            this.setState({ detailPanelWidth: width });
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (!this.resizeObserver && this.gridContainerRef.current) {
            this.observeGridContainer();
        }

        if (prevState.detailPanelExpandedRowIds !== this.state.detailPanelExpandedRowIds
            && this.state.detailPanelExpandedRowIds.length > 0) {
            this.updateDetailPanelWidth();
        }
    }

    loadUsers() {
        const api = quosal.api.admin.initApprovalGroups();
        api.finished = function (msg) {
            this.setState({ users: msg.users || [] });
        }.bind(this);
        api.call();
    }

    loadAuditLog() {
        const { page, appliedFilters } = this.state;
        this.setState({ loading: true });

        const api = quosal.api.admin.getMlaConfigAuditTrail({
            startDate: appliedFilters.startDate,
            endDate: appliedFilters.endDate,
            sourceType: appliedFilters.sourceType,
            auditAction: appliedFilters.action,
            userId: appliedFilters.userId,
            sourceName: appliedFilters.sourceName,
            pageNumber: page + 1,
            pageSize: DEFAULT_PAGE_SIZE
        });

        api.finished = function (msg) {
            const rows = (msg.results || []).map((item) => {
                if (typeof item === 'string') {
                    try {
                        return JSON.parse(item);
                    } catch {
                        return {};
                    }
                }
                return item;
            });

            this.setState({
                rows: rows,
                resultCount: msg.resultCount || 0,
                loading: false,
                detailPanelExpandedRowIds: []
            });
        }.bind(this);

        api.call();
    }

    handleFilterChange(field, value) {
        this.setState((prevState) => ({
            filters: {
                ...prevState.filters,
                [field]: value
            }
        }));
    }

    applyFilters() {
        this.setState({
            page: 0,
            appliedFilters: { ...this.state.filters },
            detailPanelExpandedRowIds: []
        }, this.loadAuditLog);
    }

    clearFilters() {
        const emptyFilters = {
            startDate: '',
            endDate: '',
            sourceType: '',
            action: '',
            userId: '',
            sourceName: ''
        };

        this.setState({
            filters: emptyFilters,
            appliedFilters: emptyFilters,
            page: 0,
            detailPanelExpandedRowIds: []
        }, this.loadAuditLog);
    }

    handlePaginationChange(model) {
        this.setState({
            page: model.page,
            detailPanelExpandedRowIds: []
        }, this.loadAuditLog);
    }

    getRowId(row) {
        return row.IdApprovalConfigAuditTrail || row.id;
    }

    handleDetailPanelExpandedRowIdsChange(detailPanelExpandedRowIds) {
        const ids = detailPanelExpandedRowIds || [];

        if (ids.length <= 1) {
            this.setState({ detailPanelExpandedRowIds: ids });
            return;
        }

        const previousIds = this.state.detailPanelExpandedRowIds;
        const newlyExpandedId = ids.find((id) => !previousIds.some((previousId) => previousId === id));

        this.setState({
            detailPanelExpandedRowIds: newlyExpandedId != null ? [newlyExpandedId] : [ids[ids.length - 1]]
        });
    }

    handleRowClick(params, event) {
        if (event.target.closest('button')) {
            return;
        }

        const rowId = params.id;
        this.setState((prevState) => {
            const isExpanded = prevState.detailPanelExpandedRowIds.some((id) => id === rowId);
            return {
                detailPanelExpandedRowIds: isExpanded ? [] : [rowId]
            };
        });

        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
    }

    renderDetailPanel(params) {
        const { detailPanelWidth } = this.state;

        return (
            <Box
                sx={{
                    width: detailPanelWidth ? `${detailPanelWidth}px` : '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    boxSizing: 'border-box'
                }}
            >
                <ApprovalAuditChangeDetails row={params.row} />
            </Box>
        );
    }

    renderFilters() {
        const { filters, users } = this.state;

        return (
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={2}>
                    <DateFilterTextField
                        fullWidth
                        size="small"
                        label="Start Date"
                        type="date"
                        value={filters.startDate}
                        onChange={(e) => this.handleFilterChange('startDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} md={2}>
                    <DateFilterTextField
                        fullWidth
                        size="small"
                        label="End Date"
                        type="date"
                        value={filters.endDate}
                        onChange={(e) => this.handleFilterChange('endDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="mla-audit-source-type-label" shrink>Type</InputLabel>
                        <Select
                            labelId="mla-audit-source-type-label"
                            label="Type"
                            displayEmpty
                            notched
                            value={filters.sourceType}
                            renderValue={(value) => getOptionLabel(SOURCE_TYPE_OPTIONS, value)}
                            onChange={(e) => this.handleFilterChange('sourceType', e.target.value)}
                        >
                            {SOURCE_TYPE_OPTIONS.map((option) => (
                                <MenuItem key={option.value || 'all-types'} value={option.value}>{option.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="mla-audit-action-label" shrink>Action</InputLabel>
                        <Select
                            labelId="mla-audit-action-label"
                            label="Action"
                            displayEmpty
                            notched
                            value={filters.action}
                            renderValue={(value) => getOptionLabel(ACTION_OPTIONS, value)}
                            onChange={(e) => this.handleFilterChange('action', e.target.value)}
                        >
                            {ACTION_OPTIONS.map((option) => (
                                <MenuItem key={option.value || 'all-actions'} value={option.value}>{option.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="mla-audit-user-label" shrink>User</InputLabel>
                        <Select
                            labelId="mla-audit-user-label"
                            label="User"
                            displayEmpty
                            notched
                            value={filters.userId}
                            renderValue={(value) => formatUserLabel(users, value)}
                            onChange={(e) => this.handleFilterChange('userId', e.target.value)}
                        >
                            <MenuItem value="">All Users</MenuItem>
                            {users.map((user) => (
                                <MenuItem key={user.id} value={user.id}>
                                    {user.PresentationName || user.UserName || `${user.FirstName || ''} ${user.LastName || ''}`.trim()}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Name Contains"
                        value={filters.sourceName}
                        onChange={(e) => this.handleFilterChange('sourceName', e.target.value)}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Stack direction="row" spacing={1}>
                        <Button variant="contained" onClick={this.applyFilters}>Apply Filters</Button>
                        <Button variant="outlined" onClick={this.clearFilters} sx={auditLogClearFiltersButtonSx}>Clear</Button>
                    </Stack>
                </Grid>
            </Grid>
        );
    }

    render() {
        const { rows, loading, resultCount, page, detailPanelExpandedRowIds } = this.state;

        const columns = [
            {
                ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
                align: 'left',
                headerAlign: 'left',
                width: 52,
                minWidth: 52,
                maxWidth: 52
            },
            {
                field: 'EventDate',
                headerName: 'Date',
                disableColumnMenu: true,
                flex: 0.63,
                minWidth: 120,
                maxWidth: 155,
                valueGetter: (value) => formatEventDateCell(value),
                valueFormatter: (value) => value?.display || '',
                renderCell: renderEventDateCell,
                sortComparator: (v1, v2) => (v1?.raw || 0) - (v2?.raw || 0)
            },
            {
                field: 'SourceType',
                headerName: 'Type',
                disableColumnMenu: true,
                flex: 0.6,
                minWidth: 90,
                valueFormatter: (value) => getSourceTypeLabel(value)
            },
            {
                field: 'SourceName',
                headerName: 'Name',
                disableColumnMenu: true,
                flex: 0.8,
                minWidth: 100
            },
            {
                field: 'Action',
                headerName: 'Action',
                disableColumnMenu: true,
                flex: 0.5,
                minWidth: 80
            },
            {
                field: 'EventInfo',
                headerName: 'Event',
                disableColumnMenu: true,
                flex: 2,
                minWidth: 160
            },
            {
                field: 'User',
                headerName: 'User',
                disableColumnMenu: true,
                flex: 0.8,
                minWidth: 100,
                valueGetter: (value, row) => formatUserName(row)
            }
        ];

        return (
            <AppTheme>
                <Box sx={{ p: 2 }}>
                    <Typography sx={{ fontSize: '18px', fontWeight: 'bold', mb: 2 }}>
                        MLA Configuration Audit Log
                    </Typography>
                    {this.renderFilters()}
                    <Box
                        ref={this.gridContainerRef}
                        sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}
                    >
                    <DataGridPro
                        autoHeight
                        rows={rows}
                        columns={columns}
                        getRowId={this.getRowId}
                        loading={loading}
                        pagination
                        paginationMode="server"
                        rowCount={resultCount}
                        paginationModel={{ page, pageSize: DEFAULT_PAGE_SIZE }}
                        pageSizeOptions={[]}
                        onPaginationModelChange={this.handlePaginationChange}
                        onRowClick={this.handleRowClick}
                        detailPanelExpandedRowIds={detailPanelExpandedRowIds}
                        onDetailPanelExpandedRowIdsChange={this.handleDetailPanelExpandedRowIdsChange}
                        disableRowSelectionOnClick
                        disableMultipleRowSelection
                        density="compact"
                        getDetailPanelContent={this.renderDetailPanel}
                        getDetailPanelHeight={() => 'auto'}
                        slots={{
                            detailPanelExpandIcon: ChevronRightIcon,
                            detailPanelCollapseIcon: ExpandMoreIcon
                        }}
                        slotProps={{
                            cell: {
                                tabIndex: -1
                            },
                            baseIconButton: {
                                disableRipple: true,
                                disableFocusRipple: true,
                                sx: auditLogGridToggleButtonSx
                            }
                        }}
                        sx={(theme) => ({
                            width: '100%',
                            maxWidth: '100%',
                            minWidth: 0,
                            color: theme.palette.text.primary,
                            ...(theme.palette.mode === 'light' && {
                                border: `1px solid ${theme.palette.quote_step.lockedFontColor}`
                            }),
                            ...(theme.palette.mode === 'dark' && {
                                borderColor: theme.palette.divider,
                                backgroundColor: theme.palette.background.default,
                                '& .MuiDataGrid-columnHeaders': {
                                    backgroundColor: theme.palette.background.paper,
                                    borderBottom: `1px solid ${theme.palette.divider}`
                                },
                                '& .MuiDataGrid-columnHeader': {
                                    backgroundColor: theme.palette.background.paper
                                },
                                '& .MuiDataGrid-columnHeaderTitle': {
                                    color: theme.palette.text.primary,
                                    fontWeight: 600
                                },
                                '& .MuiDataGrid-iconButtonContainer button': {
                                    color: theme.palette.text.primary
                                },
                                '& .MuiDataGrid-footerContainer': {
                                    backgroundColor: theme.palette.background.paper,
                                    borderTop: `1px solid ${theme.palette.divider}`
                                }
                            }),
                            '& .MuiDataGrid-main': {
                                width: '100%',
                                maxWidth: '100%'
                            },
                            '& .MuiDataGrid-virtualScroller': {
                                overflowX: 'auto'
                            },
                            '& .MuiDataGrid-detailPanel': {
                                position: 'sticky',
                                left: 0,
                                overflow: 'hidden',
                                backgroundColor: theme.palette.background.default,
                                color: theme.palette.text.primary,
                                borderTop: `1px solid ${theme.palette.divider}`
                            },
                            '& .MuiDataGrid-detailPanels': {
                                overflow: 'hidden'
                            },
                            '& .MuiDataGrid-row': {
                                cursor: 'pointer',
                                ...(theme.palette.mode === 'light' && {
                                    borderBottom: `1px solid ${theme.palette.quote_step.lockedFontColor}`,
                                    borderTop: 'none',
                                    borderLeft: 'none',
                                    borderRight: 'none'
                                }),
                                ...(theme.palette.mode === 'dark' && {
                                    backgroundColor: theme.palette.background.paper,
                                    '&:hover': {
                                        backgroundColor: theme.palette.background.ghost
                                    },
                                    '&.Mui-hovered': {
                                        backgroundColor: theme.palette.background.ghost
                                    }
                                })
                            },
                            '& .MuiDataGrid-row.Mui-selected': {
                                backgroundColor: (theme.palette.mode === 'dark'
                                    ? theme.palette.background.paper
                                    : 'inherit')
                            },
                            '& .MuiDataGrid-row.Mui-selected:hover': {
                                backgroundColor: (theme.palette.mode === 'dark'
                                    ? theme.palette.background.ghost
                                    : 'action.hover')
                            },
                            '& .MuiDataGrid-cell': {
                                ...(theme.palette.mode === 'dark' && {
                                    color: theme.palette.text.primary,
                                    backgroundColor: theme.palette.background.paper,
                                    borderColor: theme.palette.divider
                                }),
                                '&:focus': {
                                    outline: 'none'
                                },
                                '&:focus-within': {
                                    outline: 'none'
                                }
                            },
                            '& .MuiDataGrid-columnHeader:focus-within': {
                                outline: 'none'
                            },
                            '& .MuiDataGrid-columnHeader:hover': {
                                backgroundColor: (theme.palette.mode === 'dark'
                                    ? theme.palette.background.paper
                                    : 'transparent')
                            },
                            '& .MuiDataGrid-detailPanelToggleCell': {
                                ...auditLogGridToggleButtonSx,
                                justifyContent: 'flex-start'
                            },
                            [`& .MuiDataGrid-cell[data-field="${DETAIL_PANEL_TOGGLE_FIELD}"]`]: {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                overflow: 'visible',
                                px: 0.5
                            },
                            [`& .MuiDataGrid-cell[data-field="${DETAIL_PANEL_TOGGLE_FIELD}"] button`]: auditLogGridToggleButtonSx,
                            [`& .MuiDataGrid-columnHeader[data-field="${DETAIL_PANEL_TOGGLE_FIELD}"]`]: {
                                px: 0.5
                            }
                        })}
                    />
                    </Box>
                </Box>
            </AppTheme>
        );
    }
}
